import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { AddingDifferentApplicationTypeAttempt } from '~/Features/ApplicationEvents/ApplicationEvents';
import {
    AddingAnotherOptionAttempt,
    MaxNumberOfChoicesExceeded,
} from '~/Features/CampusApplication/Events/CampusApplicationEvents';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import type { Event, EventHandler } from '~/Features/Messaging/types';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { ApplicationProgressState, getApplicationProgressState } from '~/Partials/Nav/ApplicationProgressState';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import styles from './NotificationPopup.module.css';
import type { NotificationPopupViewModel } from './NotificationPopupViewModel.csharp';

interface NotificationPopupState {
    isOpen: boolean;
    eventId: string;
    applicationUrl: string;
}

export class NotificationPopup
    extends React.Component<NotificationPopupViewModel, NotificationPopupState>
    implements EventHandler<Event>
{
    constructor(props: NotificationPopupViewModel) {
        super(props);
        this.state = {
            isOpen: false,
            eventId: '',
            applicationUrl: '',
        };

        this.closePopup = this.closePopup.bind(this);
    }

    private static getViewModel(props: NotificationPopupViewModel) {
        const applicationProgressState = getApplicationProgressState();
        switch (applicationProgressState) {
            case ApplicationProgressState.CampusApplicationInProgress:
                return {
                    heading: props.campusApplicationAlreadyInProgressViewModel.heading,
                    text: props.campusApplicationAlreadyInProgressViewModel.text,
                };
            case ApplicationProgressState.OnlineApplicationInProgress:
                return {
                    heading: props.onlineApplicationAlreadyInProgressViewModel.heading,
                    text: props.onlineApplicationAlreadyInProgressViewModel.text,
                };
            default:
                return { heading: '', text: '' };
        }
    }

    componentDidMount(): void {
        MessageBroker.subscribe(this, MaxNumberOfChoicesExceeded.id);
        MessageBroker.subscribe(this, AddingAnotherOptionAttempt.id);
        MessageBroker.subscribe(this, AddingDifferentApplicationTypeAttempt.id);
        this.setCurrentApplicationUrl();
    }

    componentWillUnmount(): void {
        MessageBroker.unsubscribe(this, MaxNumberOfChoicesExceeded.id);
        MessageBroker.unsubscribe(this, AddingAnotherOptionAttempt.id);
        MessageBroker.unsubscribe(this, AddingDifferentApplicationTypeAttempt.id);
    }

    public handle(event: Event): void {
        this.setState({
            isOpen: true,
            eventId: event.id,
            applicationUrl: this.getCurrentApplicationUrl(),
        });
    }

    public render() {
        const { cancelButtonText, goToApplicationButtonText } = this.props;
        const { heading, text } = this.getMessage(this.props);
        const { isOpen, applicationUrl } = this.state;

        return (
            <FocusTrap active={isOpen}>
                <div
                    className={classNames(
                        styles.NotificationPopup,
                        isOpen ? styles['Variant--open'] : styles['Variant--closed']
                    )}
                >
                    <div className={styles.Inner}>
                        <div className={styles.Content}>
                            <Button onClick={this.closePopup} className={styles.ClosePopupIcon}>
                                <span className="visuallyHidden">{cancelButtonText}</span>
                                <SvgIcon iconName="close" />
                            </Button>
                            <p className={styles.Heading}>{heading}</p>
                            <p className={styles.Text}>{text}</p>
                            <div className={styles.Buttons}>
                                <Link
                                    className={styles.GoToApplicationButton}
                                    label={goToApplicationButtonText}
                                    href={applicationUrl}
                                    type={LinkType.Button}
                                />
                                <Button
                                    onClick={this.closePopup}
                                    className={styles.CancelButton}
                                    size={ButtonSize.Large}
                                    tier={ButtonTier.LegacyPrimary}
                                    type={ButtonType.Outline}
                                >
                                    {cancelButtonText}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </FocusTrap>
        );
    }

    private getCurrentApplicationUrl() {
        const applicationProgressState = getApplicationProgressState();
        const { campusApplicationUrl, onlineApplicationUrl } = this.props;
        switch (applicationProgressState) {
            case ApplicationProgressState.CampusApplicationInProgress:
                return campusApplicationUrl;
            case ApplicationProgressState.OnlineApplicationInProgress:
                return onlineApplicationUrl;
            default:
                return '';
        }
    }

    private setCurrentApplicationUrl(): void {
        const currentUrl = this.getCurrentApplicationUrl();
        this.setState({ applicationUrl: currentUrl });
    }

    private getMessage(props: NotificationPopupViewModel): { heading: string; text: string } {
        const { eventId } = this.state;
        switch (eventId) {
            case MaxNumberOfChoicesExceeded.id:
                return {
                    heading: props.maxNumberOfChoicesNotificationViewModel.heading,
                    text: props.maxNumberOfChoicesNotificationViewModel.text,
                };
            case AddingAnotherOptionAttempt.id:
                return {
                    heading: props.anotherOptionAlreadyAddedViewModel.heading,
                    text: props.anotherOptionAlreadyAddedViewModel.text,
                };
            case AddingDifferentApplicationTypeAttempt.id:
                return NotificationPopup.getViewModel(props);

            default:
                return { heading: '', text: '' };
        }
    }

    private closePopup() {
        this.setState({ isOpen: false });
    }
}
